import { CheckCircle2, XCircle, MinusCircle } from "lucide-react";

/**
 * Reusable per-subsystem KPI matrix table.
 * Props:
 *  - title: panel title (string)
 *  - subtitle: optional secondary line under title
 *  - items: [{subsystem, events, ...}]
 *  - columns: [{key, label, align, render(value, row), tone(value, row)}]
 *  - target: optional { label, value } for the target chip
 *  - testId: root data-testid
 */
export default function SubsystemMatrix({
  title,
  subtitle,
  items = [],
  columns = [],
  target,
  testId = "subsystem-matrix",
  panelIndex,
}) {
  return (
    <div
      className="bg-white p-6 rounded-xl border border-slate-200/60 kpi-shadow"
      data-testid={testId}
    >
      <div className="flex items-start justify-between mb-4 gap-3 flex-wrap">
        <div>
          <div className="flex items-center">
            {panelIndex != null && (
              <span className="text-[#93c5fd] mr-2 font-black text-lg">{panelIndex}.</span>
            )}
            <span className="text-[#1e40af] font-bold text-sm uppercase tracking-tight">
              {title}
            </span>
          </div>
          {subtitle && (
            <div className="text-xs text-slate-500 mt-1">{subtitle}</div>
          )}
        </div>
        {target && (
          <div
            className="text-[11px] font-bold uppercase tracking-widest px-3 py-1.5 rounded-full bg-slate-100 text-slate-700"
            data-testid={`${testId}-target-chip`}
          >
            {target.label}
          </div>
        )}
      </div>
      <div className="overflow-x-auto trams-scroll">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200">
              {columns.map((c) => (
                <th
                  key={c.key}
                  className={`py-2 px-3 font-semibold ${
                    c.align === "right" ? "text-right" : "text-left"
                  }`}
                >
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className="text-center py-6 text-slate-400"
                >
                  Tidak ada data untuk periode ini
                </td>
              </tr>
            ) : (
              items.map((row) => (
                <tr
                  key={row.subsystem}
                  className="border-b border-slate-100 hover:bg-slate-50"
                  data-testid={`${testId}-row-${row.subsystem.replace(/\s+/g, "-")}`}
                >
                  {columns.map((c) => {
                    const raw = row[c.key];
                    const rendered = c.render ? c.render(raw, row) : raw;
                    const tone = c.tone ? c.tone(raw, row) : "";
                    return (
                      <td
                        key={c.key}
                        className={`py-2.5 px-3 ${
                          c.align === "right"
                            ? "text-right tabular-nums"
                            : "text-slate-800"
                        } ${tone}`}
                      >
                        {rendered}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* -------- shared cell renderers -------- */
export function StatusPill({ ok, okLabel = "On Target", failLabel = "Below Target", neutralLabel = "No Data", neutral = false }) {
  if (neutral) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-slate-100 text-slate-500">
        <MinusCircle className="w-3 h-3" />
        {neutralLabel}
      </span>
    );
  }
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${
        ok ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"
      }`}
    >
      {ok ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
      {ok ? okLabel : failLabel}
    </span>
  );
}

export function SubsystemNameCell({ name, colorMap }) {
  const color = colorMap?.[name] || "#64748b";
  return (
    <div className="flex items-center gap-2 font-semibold text-slate-900">
      <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: color }} />
      {name}
    </div>
  );
}

export const SUBSYSTEM_COLORS = {
  Signalling: "#1e40af",
  "Rolling Stock": "#3b82f6",
  "Power Supply": "#f59e0b",
  PSD: "#22c55e",
  External: "#94a3b8",
  "Human Error": "#ef4444",
  Others: "#64748b",
};
